'use client';

import {
  DEFAULT_POPUP_OPEN_DELAY,
  DEFAULT_POPUP_REOPEN_DELAY,
  DEFAULT_POPUP_SHOW_COUNT,
  PROMO_POPUP_DELAY_DISABLED,
} from '@/constant/configs';
import useAppLocale from '@/hooks/useAppLocale';
import { PromoPopupDataType } from '@/lib/types/customer/PromoPopupDataStoreType';
import { storeFront } from '@/lib/utils/routeLinks';
import { Dialog, DialogPanel } from '@headlessui/react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

interface promoPopupProps {
  promoPopupData: PromoPopupDataType | null | undefined;
  error?: string | null;
}

function getPopUpStatus(
  startDate: string,
  endDate: string,
  currentDate = new Date()
) {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const current = new Date(currentDate);

  const expired = current > end;
  const active = current >= start && current <= end;
  const upcoming = current < start;

  return {
    expired,
    active,
    upcoming,
    status: expired ? 'expired' : active ? 'active' : 'upcoming',
  };
}

function stripLocale(path: string) {
  const regex = /^\/[a-z]{2}(-[A-Z]{2})?/;
  return path.replace(regex, '') || '/';
}

export default function PromoPopup({ promoPopupData, error }: promoPopupProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isOpenDelayDisabled =
    searchParams.has('utm_source') &&
    searchParams.get('utm_source') === PROMO_POPUP_DELAY_DISABLED;

  const excludedPaths = [
    storeFront.login.en(),
    storeFront.register.de(),
    storeFront.register.en(),
    storeFront.otp.de(),
    storeFront.forgotPassword.de(),
  ];
  const [isOpen, setIsOpen] = useState(false);
  const [shownCount, setShownCount] = useState(DEFAULT_POPUP_SHOW_COUNT);
  const [isActive, setIsActive] = useState(false);
  const reopenDelay =
    promoPopupData?.more_config?.reopen_delay ?? DEFAULT_POPUP_REOPEN_DELAY;
  const openDelay = isOpenDelayDisabled
    ? 0
    : reopenDelay ||
      (promoPopupData?.more_config?.open_delay ?? DEFAULT_POPUP_OPEN_DELAY);

  // Helper functions for session-specific storage
  const getSessionItem = (sessionId: string, key: string) => {
    if (typeof window === 'undefined') return null;
    try {
      const item = localStorage.getItem(`${sessionId}_${key}`);
      return item ? JSON.parse(item) : null;
    } catch (e) {
      return null;
    }
  };

  const setSessionItem = (sessionId: string, key: string, value: any) => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(`${sessionId}_${key}`, JSON.stringify(value));
    } catch (e) {
      console.error('Storage error', e);
    }
  };

  const removeSessionItem = (sessionId: string, key: string) => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem(`${sessionId}_${key}`);
    } catch (e) {
      console.error('Storage error', e);
    }
  };

  // Clean up localStorage from previous browser sessions
  const cleanupPreviousSessions = () => {
    if (typeof window === 'undefined') return;
    try {
      const popupKeys = Object.keys(localStorage).filter(
        (key) =>
          key.endsWith('_popupDismissed') || key.endsWith('_popupShownCount')
      );

      // Remove all popup-related session data
      popupKeys.forEach((key) => {
        localStorage.removeItem(key);
      });
    } catch (e) {
      console.error('Cleanup error', e);
    }
  };

  // Get or create browser session ID with cleanup
  const getBrowserSessionId = () => {
    if (typeof window === 'undefined') return null;

    // Try to get existing session from cookie
    const cookies = document.cookie.split(';');
    let sessionId = null;
    let foundCookie = false;

    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'browserSessionId') {
        sessionId = value;
        foundCookie = true;
        break;
      }
    }

    // Clean up previous session data if this is a new browser session
    if (!foundCookie && sessionId === null) {
      cleanupPreviousSessions();
      sessionId =
        'session_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
      // Session cookie (expires when browser closes)
      document.cookie = `browserSessionId=${sessionId}; path=/`;
    }

    return sessionId;
  };

  const [browserSessionId, setBrowserSessionId] = useState<string | null>(null);

  // Initialize browser session ID on client side only
  useEffect(() => {
    const sessionId = getBrowserSessionId();
    setBrowserSessionId(sessionId);
  }, []);

  if (error) {
    toast.error(error, { duration: 10000 });
  }

  const { active } = getPopUpStatus(
    promoPopupData?.start_at as string,
    promoPopupData?.end_at as string
  );

  const lang: string = useAppLocale().lang;

  useEffect(() => {
    if (promoPopupData && browserSessionId) {
      if (promoPopupData?.is_active && active) {
        setIsActive(true);
      } else {
        setIsActive(false);
        // Clear session storage when popup is not active
        removeSessionItem(browserSessionId, 'popupDismissed');
        removeSessionItem(browserSessionId, 'popupShownCount');
      }
    }
  }, [promoPopupData, browserSessionId]);

  // Initialize popup count from session storage
  useEffect(() => {
    if (isActive && browserSessionId) {
      try {
        const count =
          getSessionItem(browserSessionId, 'popupShownCount') ||
          DEFAULT_POPUP_SHOW_COUNT;
        setShownCount(count);
      } catch (e) {
        console.error('Storage error', e);
      }
    }
  }, [isActive, browserSessionId]);

  useEffect(() => {
    if (
      isActive &&
      browserSessionId &&
      !isOpen &&
      !excludedPaths.includes(stripLocale(pathname))
    ) {
      try {
        const dismissed =
          getSessionItem(browserSessionId, 'popupDismissed') === true;

        // Only show popup if not permanently dismissed
        if (!dismissed) {
          if (shownCount === 0) {
            // First visit - show popup after openDelay
            const openTimer = setTimeout(() => setIsOpen(true), openDelay);
            return () => clearTimeout(openTimer);
          } else if (shownCount === 1) {
            // After first close - show after reopenDelay
            const reopenTimer = setTimeout(() => setIsOpen(true), reopenDelay);
            return () => clearTimeout(reopenTimer);
          }
          // If shownCount >= 2, popup has been closed twice, don't show until browser restart
        }
      } catch (e) {
        console.error('Storage error', e);
      }
    }
  }, [
    isActive,
    shownCount,
    isOpen,
    reopenDelay,
    browserSessionId,
    pathname,
    openDelay,
  ]);

  // Handle close (X button click)
  const handleClose = () => {
    if (!browserSessionId) return;

    try {
      const newCount = shownCount + 1;
      setIsOpen(false);
      setShownCount(newCount);
      setSessionItem(browserSessionId, 'popupShownCount', newCount);
      // After 2 shows, mark as dismissed until browser restart
      if (newCount >= 2) {
        setSessionItem(browserSessionId, 'popupDismissed', true);
      }
    } catch (e) {
      console.error('Storage error', e);
    }
  };

  // Handle CTA click (Sign up button)
  const handleRegister = () => {
    if (!browserSessionId) return;

    try {
      // Permanently dismiss until browser restart
      setSessionItem(browserSessionId, 'popupDismissed', true);
    } catch (e) {
      console.error('Storage error', e);
    }
    setIsOpen(false);
    router.push(storeFront.register.de());
  };

  const termsLink =
    (promoPopupData?.more_config?.terms_link?.[lang] ||
      promoPopupData?.more_config?.terms_link?.['de']) ??
    '/';

  return (
    <Dialog
      open={isOpen}
      onClose={() => handleClose()}
      className="relative z-50"
    >
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/60" aria-hidden="true" />

      {/* Modal */}
      <div className="fixed inset-0 mt-20 flex items-center justify-center p-4 md:mt-0">
        <DialogPanel className="group relative h-fit w-[95dvw] overflow-y-auto rounded-[30px] border-8 border-gray-300 bg-white shadow-lg sm:w-[600px] md:w-[660px]">
          {/* <DialogPanel className="group relative h-fit w-[95dvw] overflow-y-auto rounded-[30px]  border-8 border-gray-300 bg-white shadow-lg sm:w-[90dvw] md:w-[70dvw] lg:w-[55dvw] xl:w-[50dvw] xl:max-w-[675px]"> */}
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute right-3 top-3 z-10 h-5 w-5 border-spacing-1 border-2 border-black bg-white bg-opacity-30 text-center font-bold leading-none text-black opacity-100 shadow transition-all hover:text-gray-700"
            aria-label="Close popup"
          >
            {/* &times; */}X
          </button>

          {/* Content */}
          <div className="flex min-h-full flex-col overflow-y-auto text-center sm:flex-row">
            {/* Left Text */}
            <div className="order-last flex h-full flex-1 flex-col px-4 py-3 text-center sm:order-none sm:w-[45%] sm:justify-center sm:px-6 sm:py-12 md:text-left">
              {/* <div className="order-last flex h-full flex-1 flex-col px-4 py-3 text-center sm:order-none sm:max-w-[50%] sm:justify-center sm:px-6 sm:py-12 md:text-left xl:max-w-[45%]"> */}
              {/* Vertically center this block */}
              {/* <div className="-mt-4 flex flex-col items-center justify-center text-center"> */}
              <div className="flex flex-col items-center justify-center text-center">
                <p className="mt-4 text-sm text-gray-500 sm:mt-0 sm:text-base md:text-lg">
                  {promoPopupData?.title ?? 'Gewinnspiel 18. - 31. August 2025'}
                </p>
                <h2 className="mt-1 text-h6 font-bold leading-tight text-pink-700 sm:text-h5 md:text-h4 2xl:max-w-[90%]">
                  {promoPopupData?.header ?? `JETZT ANMELDEN &amp; GEWINNEN!`}
                </h2>
                <div
                  className="mt-2 text-center text-sm text-gray-700 sm:mt-4 sm:text-base md:text-lg"
                  dangerouslySetInnerHTML={{
                    __html:
                      promoPopupData?.more_config?.description?.[lang] ?? '',
                  }}
                />
                {promoPopupData?.more_config?.is_button_visible && (
                  <button
                    onClick={handleRegister}
                    className="mb-2 mt-2 rounded-full bg-primary px-6 py-3 text-sm font-normal text-white hover:bg-opacity-90 sm:text-base"
                  >
                    {promoPopupData?.more_config?.button_label?.[lang] ??
                      `registrieren &amp; teilnehmen`}
                  </button>
                )}
              </div>

              {/* Bottom aligned */}
              {promoPopupData?.more_config?.is_terms_visible && (
                <div className="mt-3 text-center text-xs font-semibold underline sm:mt-3 sm:text-base">
                  {/* <div
                    className="text-gray-500"
                    dangerouslySetInnerHTML={{
                      __html: promoPopupData?.more_config?.terms_text?.[lang] ?? '',
                    }}
                  /> */}
                  {termsLink && termsLink?.startsWith('/') ? (
                    <Link href={termsLink}>
                      {promoPopupData?.more_config?.terms_label?.[lang] ??
                        `Teilnahmebedingungen`}
                    </Link>
                  ) : (
                    <a href={termsLink} target="_blank">
                      {promoPopupData?.more_config?.terms_label?.[lang] ??
                        `Teilnahmebedingungen`}
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Right Image */}
            <div className="relative order-first flex shrink-0 items-center justify-center sm:order-none sm:w-[55%] sm:py-2 sm:pr-4">
              {/* <div className="relative order-first flex items-center justify-center sm:order-none sm:max-w-[50%] sm:flex-1 sm:py-2 sm:pr-4 xl:max-w-[55%]"> */}
              {/* <div className="relative order-first flex max-h-[53dvh] items-center justify-center sm:order-none sm:max-w-[50%] sm:flex-1 sm:py-2 sm:pr-4 xl:max-w-[55%]"> */}
              {/* for desktop */}
              <div className="relative hidden h-full w-full sm:block md:w-[340px]">
                <Image
                  src={
                    promoPopupData?.more_config?.desktop_image?.[lang] ??
                    '/promo-popup/sigg.png'
                  }
                  alt={promoPopupData?.header ?? 'Ninja Creami Eismaschine'}
                  fill
                  className="h-auto rounded-lg object-contain sm:rounded-r-[30px]"
                  // className="object-contain sm:rounded-r-[30px] sm:object-fill"
                  // className="object-contain sm:rounded-r-[30px] sm:object-cover"
                  // sizes="(max-width: 768px) 100vw, 50vw" // Important for responsive
                />
              </div>
              {/* For mobile */}
              <div className="relative block h-[20vh] w-full sm:hidden">
                <Image
                  src={
                    promoPopupData?.more_config?.mobile_image?.[lang] ??
                    '/promo-popup/revell.png'
                  }
                  alt={promoPopupData?.header ?? 'Ninja Creami Eismaschine'}
                  fill
                  className="object-cover"
                  // sizes="(max-width: 768px) 100vw, 50vw" // Important for responsive
                />
              </div>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
